// Bridges the browser console (and global error events) to the central event
// server. Two design choices worth calling out:
//
//  1. The redirect is active in every build environment — the log DB is split
//     per environment (dev/test/prod each have their own event server URL via
//     VUE_APP_eventUrl and their own DB), so we don't need an in-code check
//     for the environment: each build simply logs to "its" server.
//
//  2. The original console is preserved. Every intercepted call is forwarded
//     to the browser console FIRST, then posted. Developers keep the full
//     DevTools experience and support gets the same events centrally.
//
// The endpoint is anonymous but hardened server-side (see LogController.PostClientLog):
// user is derived from the bearer token when present, not from the DTO, and
// the payload is capped and per-IP rate-limited. This file adds a bearer
// header when the user is already signed in.

const pkg = JSON.parse(decodeURIComponent(process.env.PACKAGE_JSON || "%7B%7D"));
const eventUrl = `${process.env.VUE_APP_eventUrl}api/log/postClientLog`;
const oidcStorageKey = `oidc.user:${process.env.VUE_APP_authorityUrl}:BlexonKundenportalClient`;
const systemTag = `Shop - V${pkg.version || "unknown"}`;

// Server caps at 4000 chars anyway (see LogController); trim client-side to
// avoid wasting the network round-trip on obviously oversized payloads.
const MAX_MESSAGE_LENGTH = 4000;

// Snapshot of the original console taken once, before we replace any methods.
// The redirect below always calls into this first, so a broken post pipeline
// can never hide DevTools output.
const originalConsole = {
	log: window.console.log.bind(window.console),
	debug: window.console.debug ? window.console.debug.bind(window.console) : window.console.log.bind(window.console),
	info: window.console.info.bind(window.console),
	warn: window.console.warn.bind(window.console),
	error: window.console.error.bind(window.console),
};

// Reentrancy guard: if the post path itself throws (or the browser fires the
// error event synchronously from within the post handler), calling the wrapped
// console.error from inside post would loop forever. This flag short-circuits
// that — the offending event is dropped rather than looping.
let posting = false;

function readAccessToken() {
	try {
		const raw = localStorage.getItem(oidcStorageKey);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return (parsed && parsed.access_token) || null;
	} catch (_) {
		return null;
	}
}

function formatArg(arg) {
	if (arg === undefined) return "undefined";
	if (arg === null) return "null";
	if (typeof arg === "string") return arg;
	if (arg instanceof Error) return arg.stack || `${arg.name}: ${arg.message}`;
	try {
		return JSON.stringify(arg);
	} catch (_) {
		// Circular reference, DOM node, or similar — fall back to a string coercion.
		return String(arg);
	}
}

function currentRoute() {
	// Prefer hash routing (used by the SPA router), otherwise fall back to
	// pathname+search. Return "" for the plain root — "/" alone as a prefix
	// on every entry is noise, not context.
	if (typeof window === "undefined" || !window.location) return "";
	const hash = window.location.hash || "";
	if (hash && hash !== "#" && hash !== "#/") {
		return hash.startsWith("#") ? hash.slice(1) : hash;
	}
	const path = (window.location.pathname || "") + (window.location.search || "");
	return path === "/" ? "" : path;
}

function buildMessage(args) {
	const rendered = Array.prototype.map.call(args, formatArg).join(" ");
	const route = currentRoute();
	const combined = route ? `[${route}] ${rendered}` : rendered;
	return combined.length > MAX_MESSAGE_LENGTH
		? combined.slice(0, MAX_MESSAGE_LENGTH)
		: combined;
}

function post(level, args) {
	if (posting) return;
	posting = true;
	try {
		const body = JSON.stringify({
			message: buildMessage(args),
			level,
			system: systemTag,
			// user is intentionally omitted — the server derives it from the bearer
			// token (spoof-proof) and ignores any client-supplied value.
		});
		const token = readAccessToken();
		const headers = { "Content-Type": "application/json" };
		if (token) headers.Authorization = `Bearer ${token}`;

		// keepalive lets the request survive the page being closed / navigated
		// away from — important because "the tab crashed" errors would otherwise
		// die with the tab. The .catch is a no-op: there is nothing meaningful
		// we can do about a failing log post, and logging the failure would loop.
		fetch(eventUrl, {
			method: "POST",
			headers,
			body,
			keepalive: true,
			credentials: "omit",
		}).catch(() => {});
	} catch (_) {
		// Never let the log path itself throw into the caller.
	} finally {
		posting = false;
	}
}

// Redirect console methods. Original output is always emitted first, so the
// central redirect can only ever ADD to what a developer sees, never remove.
const levelForMethod = {
	log: "Debug",
	debug: "Debug",
	info: "Information",
	warn: "Warning",
	error: "Error",
};

Object.keys(levelForMethod).forEach((method) => {
	window.console[method] = function (...args) {
		originalConsole[method](...args);
		post(levelForMethod[method], args);
	};
});

// Global error handlers. We route straight to post() instead of going through
// the wrapped console.error to keep the path short and avoid extra hops that
// could interact badly with the reentrancy guard. Both onerror and the "error"
// listener are common enough to fire on the same event in some browsers;
// posting.guard drops the duplicate.
window.addEventListener("error", function (event) {
	// Both a script error and a resource load failure fire "error"; resource
	// failures have no message/error, only event.target — skip those.
	if (!event.message && !event.error && !event.filename) return;
	const detail =
		event.error && event.error.stack
			? event.error.stack
			: event.message || "Unknown error";
	const where = event.filename
		? ` at ${event.filename}:${event.lineno}:${event.colno}`
		: "";
	post("Error", [detail + where]);
});

window.addEventListener("unhandledrejection", function (event) {
	const reason = event.reason;
	const detail =
		reason instanceof Error
			? reason.stack || `${reason.name}: ${reason.message}`
			: formatArg(reason);
	post("Error", [`Unhandled promise rejection: ${detail}`]);
});
